import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import type { ChatResultData, ChatResultItem } from "shared/types";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

export type GroupedMessages = Record<string, ChatResultItem[]>;

function getDateKey(date: string): string {
  if (dayjs(date).isToday()) return "Today";
  if (dayjs(date).isYesterday()) return "Yesterday";
  return dayjs(date).format("DD MMM YYYY");
}

export function groupMessagesByDate(
  messages: ChatResultItem[],
): GroupedMessages {
  return messages.reduce<GroupedMessages>((groups, message) => {
    const key = getDateKey(message.created_at);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].unshift(message);
    return groups;
  }, {});
}

export function mergeNewMessages(
  prev: GroupedMessages | null | undefined,
  newMessages: ChatResultItem[],
): GroupedMessages {
  const merged = { ...prev };

  for (const message of newMessages) {
    const key = getDateKey(message.created_at);

    if (!merged[key]) {
      merged[key] = [];
    }

    const existingIndex = merged[key].findIndex(
      (old) => old.chat_id === message.chat_id,
    );

    if (existingIndex === -1) {
      merged[key].push(message);
    } else {
      merged[key].splice(existingIndex, 1, message);
    }
  }

  return merged;
}

export function flattenInfinitePages(
  pages: ChatResultData[] | undefined,
): ChatResultItem[] {
  if (!pages) return [];
  const seen = new Set<string>();
  const result: ChatResultItem[] = [];
  for (const page of pages) {
    for (const msg of page.messages) {
      if (!seen.has(msg.chat_id)) {
        seen.add(msg.chat_id);
        result.push(msg);
      }
    }
  }
  return result;
}

export function groupedMessagesHasChatId(
  grouped: GroupedMessages | null | undefined,
  chatId: string,
): boolean {
  if (!grouped) return false;
  for (const msgs of Object.values(grouped)) {
    if (msgs.some((m) => m.chat_id === chatId)) return true;
  }
  return false;
}
